import { useEffect, useState } from 'react';
import { useSettings } from './SettingsContext';
import {
  FEATURES,
  FEATURE_GROUPS,
  type FeatureGroup,
  type FeatureId,
} from './features';
import { useT } from '../i18n/i18n';

export default function FeaturesSection() {
  const t = useT();
  const { settings, update } = useSettings();
  const [draft, setDraft] = useState<Record<FeatureId, boolean>>(settings.features);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setDraft(settings.features); }, [settings.features]);

  function toggle(id: FeatureId, on: boolean) {
    setDraft((d) => ({ ...d, [id]: on }));
    setSaved(false);
    setError(null);
  }

  async function save() {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await update({ features: draft });
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Could not save the features.'));
    } finally {
      setBusy(false);
    }
  }

  const ready = FEATURES.filter((f) => f.ready);
  const onNow = ready.filter((f) => draft[f.id]).length;
  const groups = Object.keys(FEATURE_GROUPS) as FeatureGroup[];

  return (
    <div className="card">
      <h2>{t('Features')}</h2>
      <p className="field-hint" style={{ marginTop: 0 }}>
        {t('Turn on only what this account needs. Everything here can be changed at any time, and a program can be set differently from the account it belongs to.')}
      </p>

      {error && <div className="error" role="alert">{error}</div>}

      {groups.map((group) => {
        const inGroup = FEATURES.filter((f) => f.group === group);
        if (inGroup.length === 0) return null;
        return (
          <div key={group} className="feature-group">
            <h3>{t(FEATURE_GROUPS[group])}</h3>
            {inGroup.map((f) => (
              <div className="field" key={f.id}>
                <label className={`check${f.ready ? '' : ' is-pending'}`}>
                  <input
                    type="checkbox"
                    checked={Boolean(draft[f.id])}
                    disabled={!f.ready}
                    onChange={(e) => toggle(f.id, e.target.checked)}
                  />
                  {t(f.label)}
                  {!f.ready && <span className="pill">{t('Being built')}</span>}
                </label>
                <span className="field-hint">{t(f.hint)}</span>
              </div>
            ))}
          </div>
        );
      })}

      <div className="form-actions">
        <button className="btn" onClick={save} disabled={busy}>
          {busy ? t('Saving…') : t('Save features')}
        </button>
        <span className="muted">
          {ready.length === 1
            ? t('{on} of {total} ready feature on', { on: onNow, total: ready.length })
            : t('{on} of {total} ready features on', { on: onNow, total: ready.length })}
        </span>
        {saved && <span className="muted" role="status">{t('Saved.')}</span>}
      </div>
    </div>
  );
}
